import type { NetworkTableAreaRow, NetworkTableDmaRow, NetworkTablePoiRow } from '@/api/networkTableApi';
import {
    normalisePoiStatus,
    type PoiFeatureProperties,
    type PoiStatus,
} from '@/components/map/utils/poiFeatureProperties';

/**
 * Converts a Network Overview table POI row into the `PoiFeatureProperties` shape the rest of the
 * portal already speaks, so the table's inline summary, Copy and Show Details all read from ONE
 * mapping — and Show Details reuses the existing details panels with no per-POI details call.
 *
 * Contract:
 *   - **Exhaustive at compile time** (`satisfies PoiFeatureProperties`): adding a field to the
 *     contract breaks this build rather than silently shipping an undefined.
 *   - **No manufactured values.** A null stays null; nothing becomes '' or a placeholder string.
 *     The Unassigned groups carry `area`/`dma_ident`/`dma_name` as null, which is what they are.
 *   - Hierarchy context comes from the parent rows — a POI row does not repeat it.
 *   - Under the entity contract the properties mirror the row's map feature: the id is the
 *     server-supplied `mapEntityId` (a child row's marker is its `leak:`-prefixed leak feature,
 *     so Locate highlights and Show Details target the same feature a map click would) and the
 *     status is the matching split-model entity status. Against a pre-split API the entity
 *     fields are absent and the legacy mapping is unchanged.
 */
export interface TablePoiContext {
    area: NetworkTableAreaRow;
    dma: NetworkTableDmaRow;
}

export function mapTablePoiToFeatureProperties(
    row: NetworkTablePoiRow,
    { area, dma }: TablePoiContext,
): PoiFeatureProperties {
    const isLeakRow = row.mapEntityKind === 'leak';
    const entityStatus = isLeakRow ? row.leakEntityStatus : row.poiEntityStatus;
    return {
        id: row.mapEntityId ?? row.id,
        // The server already sends the canonical map status; normalising is belt-and-braces
        // against an unexpected value, matching every other consumer.
        status: (entityStatus as PoiStatus | null | undefined) ?? normalisePoiStatus(row.status),
        entity_type: row.mapEntityKind === undefined ? undefined : isLeakRow ? 'leak' : 'poi',
        source_poi_id: row.mapEntityKind === undefined ? undefined : row.id,
        suffix_label: row.suffixLabel,
        name: row.worklist,
        group_id: row.groupId,
        stop: row.stop,
        area: area.isUnassigned ? null : area.name,
        dma_ident: dma.isUnassigned ? null : dma.ident,
        dma_name: dma.isUnassigned ? null : dma.name,
        is_planned: row.isPlanned,
        what3words: row.whatThreeWords,
        pipe_type: row.pipeType,
        assumed_leakage_rate: row.assumedLeakageRate,
        measured_leakage_rate: row.measuredLeakageRate,
        leak_what3words: row.leakWhatThreeWords,
        surveyed_date: row.surveyedDate,
        search_date: row.searchDate,
        is_leak_visible: row.isLeakVisible,
        nodig_fix: row.nodigFix,
        repaired: row.repaired,
    } satisfies PoiFeatureProperties;
}

/** The POI's map coordinate, in the [lng, lat] order every map helper expects. */
export function tablePoiCoords(row: NetworkTablePoiRow): [number, number] {
    return [row.longitude, row.latitude];
}
